import { Resource } from '@travelwin/core';
import { type TFunction } from 'i18next';

import alternativeJson from '../assets/alternative-names.json';

interface AlternativeName {
  [countryCode: string]: {
    all: string[];
    [locale: string]: string[] | undefined; // Allows any locale key like 'pl', 'en', etc.;
  };
}

const defaultAlternativeNames: AlternativeName = alternativeJson;

export const GLOBAL_DISPLAY_ORDER = -100;

// text property represents country name translated to the current locale
type Translated<T> = T & { text: string };

const sortOptions = <T extends Resource>(
  countries: Translated<T>[],
  inputValue: string | null,
): Translated<T>[] => {
  let compareFunction: (a: Translated<T>, b: Translated<T>) => number;
  if (inputValue) {
    compareFunction = (a, b) => {
      const text1 = a.text || '';
      const text2 = b.text || '';
      const text1StartsWith = isTextStartWith(text1, inputValue);
      const text2StartsWith = isTextStartWith(text2, inputValue);

      if (text1StartsWith && !text2StartsWith) return -1; // item1 comes before item2
      if (!text1StartsWith && text2StartsWith) return 1; // item2 comes before item1
      return text1.localeCompare(text2); // Sort alphabetically
    };
  } else {
    compareFunction = countriesComparator;
  }
  return countries.sort(compareFunction);
};

const countriesComparator = <T extends Resource>(
  a: Translated<T>,
  b: Translated<T>,
) => {
  const text1 = a.text || '';
  const text2 = b.text || '';
  const order1 = a?.display_order || 0;
  const order2 = b?.display_order || 0;
  if (order1 < 0 && order2 >= 0) {
    return 1;
  } else if (order1 >= 0 && order2 < 0) {
    return -1;
  } else if (order1 === GLOBAL_DISPLAY_ORDER) {
    return 1;
  } else if (order2 === GLOBAL_DISPLAY_ORDER) {
    return -1;
  }
  return text1.localeCompare(text2);
};

export const sortCountries = <T extends Resource>(
  t: TFunction,
  countries: T[],
): T[] => {
  const options = translateCountries(t, countries);

  return options.toSorted(countriesComparator);
};

const filterItems = <T extends Resource>(
  data: Translated<T>[],
  inputValue: string | null,
) => {
  if (!inputValue) return data;

  return data.filter(
    (item) =>
      (item.text || '').toLowerCase().indexOf(inputValue.toLowerCase().trim()) >
      -1,
  );
};

const isTextStartWith = (text: string, inputValue: string) =>
  text.toLowerCase().startsWith(inputValue.toLowerCase());

export const prepareCountriesOptions = ({
  t,
  lang,
  data,
  inputValue,
  alternativeNames = defaultAlternativeNames,
}: {
  t: TFunction;
  lang: string;
  data: Resource[];
  inputValue: string | null;
  alternativeNames?: AlternativeName;
}): Resource[] => {
  const translatedData = translateCountries(t, data);
  const filteredData = filterItems(translatedData, inputValue);
  const alternativeCountryCodes = findCountryCodesByAlternativeName({
    alternativeNames,
    inputValue,
    lang,
  });
  const alternativeData = translatedData.filter(
    (country) =>
      alternativeCountryCodes.includes(country.alias) &&
      !filteredData.some((item) => item.alias === country.alias),
  );
  const finalData = [...filteredData, ...alternativeData];
  return sortOptions(finalData, inputValue);
};

const findCountryCodesByAlternativeName = ({
  lang,
  alternativeNames,
  inputValue,
}: {
  lang: string;
  alternativeNames: AlternativeName;
  inputValue: string | null;
}): string[] => {
  if (!inputValue) return [];

  const matchingCodes: string[] = [];

  for (const [code, { all, ...locales }] of Object.entries(alternativeNames)) {
    if (
      all.some((item) =>
        item.toLowerCase().startsWith(inputValue.toLowerCase()),
      ) ||
      locales[lang]?.some((item) =>
        item.toLowerCase().startsWith(inputValue.toLowerCase()),
      )
    ) {
      matchingCodes.push(code);
    }
  }

  return matchingCodes;
};

const translateCountries = <T extends Resource>(
  t: TFunction,
  countries: T[],
): Translated<T>[] => {
  const { getCountryNameTranslation } = getCountryUtils(t);

  return countries.map((item) => {
    return {
      ...item,
      text: getCountryNameTranslation(item.description),
    };
  });
};

export const getCountryUtils = (t: TFunction) => {
  const getCountryName = (countryKey: string) => {
    return t(countryKey, { lng: 'en', ns: 'countries' });
  };

  const getCountryNameTranslation = (countryKey: string) => {
    return t(countryKey, { ns: 'countries' });
  };

  return {
    getCountryName,
    getCountryNameTranslation,
  };
};
